/**
 * PanelRenderer — the single bridge from a dashboard component spec to a live
 * visualization. Used by BOTH imported dashboards and the app's built-in
 * screens, so every panel everywhere goes through one data + render path.
 *
 * A component renders a data-bound panel when its `props.dataBinding` is a valid
 * {@link DataBinding}; otherwise it falls back to `fallback` (e.g. the existing
 * placeholder rendering) so untouched components behave as before. Each panel is
 * isolated by an error boundary so one failure can't blank the dashboard.
 */

import React from "react";
import type { DashboardComponent } from "../components/DashboardImporter";
import { asDataBinding, type ResolvedRange } from "./spec";
import TimeSeriesPanel from "./panels/TimeSeriesPanel";
import GaugePanel from "./panels/GaugePanel";
import StatPanel from "./panels/StatPanel";
import PanelErrorBoundary from "./PanelErrorBoundary";

interface PanelRendererProps {
  component: DashboardComponent;
  /** The dashboard-level time range (live or a window). */
  range: ResolvedRange;
  /** Fallback machine key when a binding uses a relative/blank machineRef. */
  machineBK?: string;
  /** Rendered when the component has no data binding. */
  fallback?: React.ReactNode;
}

const PanelRenderer: React.FC<PanelRendererProps> = ({
  component,
  range,
  machineBK,
  fallback,
}) => {
  const binding = asDataBinding(component.props?.dataBinding);
  if (!binding || binding.channels.length === 0) return <>{fallback ?? null}</>;

  // Allow a component to inherit the surrounding machine when its ref is blank.
  const resolved =
    binding.machineRef && binding.machineRef !== ""
      ? binding
      : { ...binding, machineRef: machineBK ?? binding.machineRef };

  let panel: React.ReactNode;
  switch (component.type) {
    case "gauge":
      panel = (
        <GaugePanel
          binding={resolved}
          range={range}
          gaugeRange={component.props?.gaugeRange}
        />
      );
      break;
    case "kpi":
      panel = (
        <StatPanel
          title={component.props?.title}
          binding={resolved}
          range={range}
        />
      );
      break;
    case "chart":
    default:
      panel = (
        <TimeSeriesPanel
          title={component.props?.title}
          binding={resolved}
          range={range}
          machineBK={resolved.machineRef}
        />
      );
  }

  return (
    <PanelErrorBoundary title={component.props?.title}>
      {panel}
    </PanelErrorBoundary>
  );
};

export default PanelRenderer;
